import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Fallback Error Component
 * Used when the main ErrorDisplay can't render due to context issues
 * No dependencies on React context or custom hooks
 */
export function FallbackError({
  error,
  errorId,
  onRetry,
}: {
  error?: Error;
  errorId?: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 font-mono text-green-400">
      <div className="w-full max-w-2xl rounded-xl border border-green-700 bg-green-950 text-center shadow-lg">
        <div className="space-y-8 p-6 sm:p-10">
          {/* Error Icon */}
          <div className="mx-auto h-16 w-16 text-green-400 sm:h-20 sm:w-20">
            <AlertTriangle className="h-full w-full" />
          </div>

          {/* Error Title */}
          <div className="space-y-6">
            <h1 className="text-xl leading-tight font-bold text-green-400 sm:text-2xl md:text-3xl">
              Something went wrong
            </h1>

            {/* Error Message */}
            <p className="mx-auto max-w-xl text-base leading-relaxed text-green-300 sm:text-lg">
              An unexpected error occurred. Don't worry, we're on it!
            </p>
          </div>

          {/* Error Details (Development) */}
          {process.env.NODE_ENV === "development" && error && (
            <details className="text-left">
              <summary className="mb-4 cursor-pointer text-center text-sm text-green-300 sm:text-base">
                Technical Details {errorId && `(ID: ${errorId})`}
              </summary>
              <pre className="max-h-40 overflow-auto rounded-lg border border-green-800 bg-green-950/20 p-4 text-xs text-green-300 sm:text-sm">
                <code className="font-mono break-words whitespace-pre-wrap">
                  {error.stack || error.message}
                </code>
              </pre>
            </details>
          )}

          {/* Production Error Details */}
          {process.env.NODE_ENV === "production" && errorId && (
            <div className="rounded-lg border border-green-700/20 bg-green-950/20 p-4">
              <p className="text-sm text-green-400/70 sm:text-base">
                Technical Details (ID: {errorId})
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center sm:gap-6">
            <button
              onClick={onRetry}
              className="flex items-center justify-center gap-3 rounded-xl bg-green-700 px-6 py-3 text-base font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-green-600 focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-green-950 focus:outline-none sm:text-lg"
            >
              <RefreshCw className="h-5 w-5" />
              <span className="whitespace-nowrap">Try Again</span>
            </button>

            <button
              onClick={() => {
                window.location.href = "/";
              }}
              className="flex items-center justify-center gap-3 rounded-xl bg-green-800 px-6 py-3 text-base font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-green-700 focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-green-950 focus:outline-none sm:text-lg"
            >
              <Home className="h-5 w-5" />
              <span className="whitespace-nowrap">Go Home</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
